/**
 * Capacidade configurada: FTE disponível e produtividade.
 *
 * São campos independentes. O departamento herda da organização só o campo
 * que ele mesmo não preencheu. Linha expirada, futura ou inativa não entra.
 *
 * Não usa quantidade de pessoas, cargo_config.fte_base, WIP nem throughput.
 * O padrão visual 3 FTE × 1 tarefa/pessoa/h não é esta configuração.
 */

export type CapacityFieldSource = 'department' | 'organization';

export interface CapacityParameterRecord {
  id: string;
  organizationId: string;
  departmentId: string | null;
  availableFte: number | null;
  productivityTasksPerPersonHour: number | null;
  validFrom: string;
  validTo: string | null;
  active: boolean;
}

export interface ResolvedCapacityField {
  value: number;
  source: CapacityFieldSource;
  recordId: string;
}

export interface ResolvedCapacityParameters {
  availableFte: ResolvedCapacityField | null;
  productivityTasksPerPersonHour: ResolvedCapacityField | null;
}

export function isCapacityParameterInForce(record: CapacityParameterRecord, onDate: string): boolean {
  if (!record.active) return false;
  if (record.validFrom > onDate) return false;
  if (record.validTo != null && record.validTo < onDate) return false;
  return true;
}

function latest(records: CapacityParameterRecord[]): CapacityParameterRecord | null {
  return [...records].sort((a, b) => {
    const byStart = b.validFrom.localeCompare(a.validFrom);
    if (byStart !== 0) return byStart;
    return b.id.localeCompare(a.id);
  })[0] ?? null;
}

function pickField(
  department: CapacityParameterRecord | null,
  organization: CapacityParameterRecord | null,
  read: (record: CapacityParameterRecord) => number | null,
): ResolvedCapacityField | null {
  const departmentValue = department ? read(department) : null;
  if (department && departmentValue != null) {
    return { value: departmentValue, source: 'department', recordId: department.id };
  }
  const organizationValue = organization ? read(organization) : null;
  if (organization && organizationValue != null) {
    return { value: organizationValue, source: 'organization', recordId: organization.id };
  }
  return null;
}

export function resolveCapacityParameters(
  records: CapacityParameterRecord[],
  scope: { departmentId: string | null },
  onDate: string,
): ResolvedCapacityParameters {
  const inForce = records.filter((record) => isCapacityParameterInForce(record, onDate));
  const department = scope.departmentId
    ? latest(inForce.filter((record) => record.departmentId === scope.departmentId))
    : null;
  const organization = latest(inForce.filter((record) => record.departmentId == null));
  return {
    availableFte: pickField(department, organization, (record) => record.availableFte),
    productivityTasksPerPersonHour: pickField(
      department,
      organization,
      (record) => record.productivityTasksPerPersonHour,
    ),
  };
}

export function capacityFieldSourceLabel(source: CapacityFieldSource): string {
  return source === 'department' ? 'Configuração do departamento' : 'Herdado da organização';
}
